import React, { useState } from 'react';
import { trackFormSubmission, trackError } from '../services/analytics';
import { sendContactEmail } from '../api/contact';
import { TextInput, Textarea, Button, Text, Alert } from '@mantine/core';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import classes from './ContactForm.module.css';

export default function ContactForm() {
  const [loading, setLoading] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setVerificationRequired(false);
    
    // Make sure reCAPTCHA is ready
    if (!executeRecaptcha) {
      notifications.show({
        title: 'Error',
        message: 'reCAPTCHA not loaded. Please refresh the page and try again.',
        color: 'red',
      });
      setLoading(false);
      return;
    }

    // Execute reCAPTCHA and get token
    const token = await executeRecaptcha('contact_form');
    
    // Get form data directly from the form elements
    const data = {
      name: event.target.name.value,
      email: event.target.email.value,
      phone: event.target.phone.value,
      message: event.target.message.value,
      recaptchaToken: token
    };

    try {
      await sendContactEmail(data);

      // Track successful form submission
      trackFormSubmission('contact', true);

      notifications.show({
        title: 'Message Sent',
        message: 'Thanks for reaching out! We\'ll get back to you soon.',
        color: 'green',
      });
      event.target.reset();
    } catch (error) {
      // Track form error
      trackError('ContactForm', error.message);
      trackFormSubmission('contact', false);
      
      // Check if this is a verification required response
      if (error.message && error.message.includes('verification required')) {
        setVerificationRequired(true);
        notifications.show({
          title: 'Verification Required',
          message: 'Please check your email to verify your submission.',
          color: 'blue',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: error.message || 'Something went wrong. Please try again.',
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={classes.form}
    >
      <form onSubmit={handleSubmit}>
        <TextInput
            label="Name"
            placeholder="Your name"
            name="name"
            required
            classNames={{
              root: classes.inputRoot,
              input: classes.input,
              label: classes.label,
              required: classes.required
            }}
            size="lg"
          />
          <TextInput
            label="Email"
            placeholder="your@email.com"
            name="email"
            required
            classNames={{
              root: classes.inputRoot,
              input: classes.input,
              label: classes.label,
              required: classes.required
            }}
            size="lg"
            mt="lg"
          />
          <TextInput
            label="Phone"
            placeholder="Your phone number"
            name="phone"
            classNames={{
              root: classes.inputRoot,
              input: classes.input,
              label: classes.label,
              required: classes.required
            }}
            size="lg"
            mt="lg"
          />
          <Textarea
            label="Message"
            placeholder="How can we help you?"
            name="message"
            required
            minRows={6}
            classNames={{
              root: classes.inputRoot,
              input: classes.textarea,
              label: classes.label,
              required: classes.required
            }}
            size="lg"
            mt="lg"
          />

          {verificationRequired && (
            <Alert color="blue" title="Verification Required" mt="lg">
              <Text size="sm">We've sent a verification email to confirm your submission. Please check your inbox.</Text>
            </Alert>
          )}
          
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              type="submit"
              size="lg"
              fullWidth
              mt="xl"
              loading={loading}
              className={classes.animatedButton}
              disabled={verificationRequired}
            >
              Send Message
            </Button>
          </motion.div>
          
          <Text size="xs" color="dimmed" mt="md" ta="center">
            This site is protected by reCAPTCHA and the Google
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a> and
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer"> Terms of Service</a> apply.
          </Text>
      </form>
    </motion.div>
  );
}
